/*
 * Component for displaying and updating metrics for a subtask
 */

import {AsyncPipe} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSliderModule} from '@angular/material/slider';
import {MatTooltipModule} from '@angular/material/tooltip';
import {select, Store} from '@ngrx/store';
import {BehaviorSubject, map, Observable, Subscription, take} from 'rxjs';
import {CurrentSkedTask, Subtask} from 'src/app/core/model/task';

import {TrackerMetric} from 'src/app/core/model/tracker';
import {selectActiveUserRole} from 'src/app/core/store/active-user/active-user.selector';
import {
  ClearMetricFormStateAction,
  UpdateMetricFormStateAction,
} from 'src/app/core/store/current-sked/current-sked.action';
import {selectMetricFormState} from 'src/app/core/store/current-sked/current-sked.selector';
import {MetricFormValue, SubtaskFormState} from 'src/app/core/store/current-sked/current-sked.state';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphDurationInputComponent} from 'src/app/shared/design/oph-duration-input/oph-duration-input.component';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphTimestampDialogComponent} from 'src/app/shared/dialog/oph-timestamp-dialog/oph-timestamp-dialog.component';
import {UserHasRoleOptionDirective} from 'src/app/shared/directives/user-has-role-option.directive';
import {PipesModule} from 'src/app/shared/pipes/pipes.module';
import {TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';
import {TaskLoadingState} from '../../shared/model/task-loading-state';
import {CurrentSkedDetailUpdateButtonComponent} from '../update-button/current-sked-detail-update-button.component';
import {RolePermissionOptions} from './../../../core/model/role';
import {MetricNumericType} from './../../../trackers/shared/tracker-metric-types';
import {CurrentSkedMetricPhotoUploadComponent} from './current-sked-metric-photo-upload/current-sked-metric-photo-upload.component';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'current-sked-task-detail-metric-update-list',
  standalone: true,
  imports: [
    OphButtonModule,
    MatMenuModule,
    OphIconModule,
    MatIconModule,
    AsyncPipe,
    PipesModule,
    MatProgressSpinnerModule,
    CurrentSkedDetailUpdateButtonComponent,
    MatTooltipModule,
    ReactiveFormsModule,
    MatIconModule,
    MatSliderModule,
    CurrentSkedMetricPhotoUploadComponent,
    OphDurationInputComponent,
    UserHasRoleOptionDirective,
  ],
  templateUrl: './current-sked-task-detail-metric-update-list.component.html',
  styleUrl: './current-sked-task-detail-metric-update-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentSkedTaskDetailMetricUpdateListComponent implements OnChanges, OnDestroy, AfterViewInit {
  private readonly mobileService = inject(MobileService);
  @Input() task: CurrentSkedTask;
  @Input() subtask: Subtask;
  @Input() loading: TaskLoadingState;
  @Input() subtaskIndex: number;
  @Input() subtaskNotAvailable: boolean;
  @Input() areAllMetricsReadOnly: boolean;
  @Input() notInSked: boolean;
  @Input() taskExpired: boolean;

  @Output() updateMetrics = new EventEmitter<{
    metrics: {metricId: string; value: string}[];
    timestamp?: string;
  }>();

  userRole$: Observable<string> = this.store$
    .pipe(select(selectActiveUserRole))
    .pipe(map(role => role?.displayName?.toLowerCase()));

  form = new FormArray([]);
  readonly dialog = inject(MatDialog);
  TrackerMetricTypes = TrackerMetricTypes;
  MetricNumericType = MetricNumericType;
  parseFloat = parseFloat;
  showUpdateButton = false;
  filteredMetrics: TrackerMetric[] = [];

  @ViewChildren('editableSpanNumeric') editableSpansNumeric: QueryList<ElementRef>;
  @ViewChildren('editableSpanText') editableSpansText: QueryList<ElementRef>;
  @ViewChildren('textInput') textInputs: QueryList<ElementRef>;

  openMenuId: string | null = null;
  focusedTextMetricIndex = signal<number | null>(null);
  booleanButtonGroupOptions = [
    {value: '0', display: 'No'},
    {value: '1', display: 'Yes'},
  ];
  booleanButtonStyle = {};

  tooltipText = 'All Metrics must be updated';
  timestamp$ = new BehaviorSubject<string>('');
  formValid$ = new BehaviorSubject<boolean>(false);
  private formSubscription: Subscription;
  private isLoadingState = false;
  rolePermissionOptions = RolePermissionOptions;

  // Validation errors per metric index: { index: 'min: X' | 'max: X' | null }
  metricValidationErrors = signal<Record<number, string | null>>({});

  constructor(private store$: Store) {
    this.formSubscription = this.form.valueChanges.subscribe(values => {
      this.validateForm();
      // Only save form state if we're not currently loading state from store
      if (!this.isLoadingState) {
        this.saveFormState();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.subtaskNotAvailable) {
      this.tooltipText = this.subtaskNotAvailable
        ? 'Subtasks must be completed in order'
        : 'All Metrics must be updated';
    }

    if (changes.subtask && this.subtask) {
      this.form.clear();

      if (this.subtask?.tracker?.metrics?.length) {
        this.filteredMetrics = this.subtask.tracker.metrics.filter(m => m.updateable);
      } else {
        this.filteredMetrics = [];
      }

      this.filteredMetrics.forEach(metric => {
        this.addMetricFormItem(metric);
      });

      this.showUpdateButton = this.filteredMetrics.some(metric => metric.updateable);

      // Check for existing form state and load it
      this.loadFormState();

      this.validateForm();

      // Update editable span values after form is initialized
      setTimeout(() => {
        this.updateEditableSpanValues();
      }, 0);
    }
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    // Set initial values for editable spans after view is initialized
    this.updateEditableSpanValues();
  }

  private shouldIncludeMetricInValidation(item: MetricFormValue, index: number): boolean {
    if (item.readOnly) return false;

    const metric = this.filteredMetrics[index];
    if (!metric) return true;

    // Exclude numeric metrics with range or stepper type from validation
    return !(
      metric.type === TrackerMetricTypes.Numeric &&
      (metric.numericType === MetricNumericType.Range || metric.numericType === MetricNumericType.Stepper)
    );
  }

  private isValidValue(value: unknown): boolean {
    return value !== null && value !== '';
  }

  private validateMetricRange(value: string | null, metric: TrackerMetric): string | null {
    if (!value || value.trim() === '') {
      return null; // Empty values are handled by isValidValue
    }

    // Only validate numeric basic type metrics
    if (metric.type !== TrackerMetricTypes.Numeric || metric.numericType !== MetricNumericType.Basic) {
      return null;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return null;
    }

    if (metric.min !== undefined && metric.min !== null && metric.min !== '') {
      const minValue = parseFloat(metric.min);
      if (!isNaN(minValue) && numValue < minValue) {
        return `min: ${metric.min}`;
      }
    }

    if (metric.max !== undefined && metric.max !== null && metric.max !== '') {
      const maxValue = parseFloat(metric.max);
      if (!isNaN(maxValue) && numValue > maxValue) {
        return `max: ${metric.max}`;
      }
    }

    return null;
  }

  validateForm(): void {
    const formValues = this.form.value;
    const metricsToValidate = formValues.filter((item, index) => this.shouldIncludeMetricInValidation(item, index));
    const hasRequiredValues = metricsToValidate.every(item => this.isValidValue(item.value));

    // Validate min/max ranges for all metrics
    const errors: Record<number, string | null> = {};
    let hasRangeErrors = false;

    this.filteredMetrics.forEach((metric, index) => {
      const formValue = this.form.at(index)?.get('value')?.value;
      const error = this.validateMetricRange(formValue, metric);
      errors[index] = error;
      if (error) {
        hasRangeErrors = true;
      }
    });

    this.metricValidationErrors.set(errors);
    this.formValid$.next(hasRequiredValues && !hasRangeErrors);
  }

  addMetricFormItem(metric: TrackerMetric) {
    const subtaskMetric = this.subtask?.tracker?.metrics?.find(m => m._id === metric._id);
    const initialValue = subtaskMetric ? this.getInitialValueForMetric(subtaskMetric) : null;

    this.form.push(
      new FormGroup({
        metricId: new FormControl(metric._id),
        value: new FormControl(initialValue),
        readOnly: new FormControl(metric.readOnly),
      })
    );
  }

  private getInitialValueForMetric(metric: TrackerMetric): string | null {
    // Only handle numeric types
    if (metric.type !== TrackerMetricTypes.Numeric || !metric.numericType) {
      return null;
    }

    // Handle range type
    if (metric.numericType === MetricNumericType.Range) {
      return metric.min ?? '';
    }

    // Handle stepper type
    if (metric.numericType === MetricNumericType.Stepper) {
      return this.getStepperInitialValue(metric);
    }

    // Handle basic numeric type
    return '';
  }

  private getStepperInitialValue(metric: TrackerMetric): string {
    // If both min and max are defined, check if 0 is in range
    if (metric.min && metric.max) {
      const min = parseFloat(metric.min);
      const max = parseFloat(metric.max);

      // If 0 is not in range, start at min
      if (min > 0 || max < 0) {
        return metric.min;
      }

      // If 0 is in range, start at 0
      return '0';
    }

    // If only min is defined, start at min
    if (metric.min) {
      return metric.min;
    }

    // No constraints, start at 0
    return '0';
  }

  onUpdateMetrics() {
    const updateData: {
      metrics: {metricId: string; value: string}[];
      timestamp?: string;
    } = {
      metrics: this.form.value.map(item => ({
        metricId: item.metricId,
        value: String(item.value),
      })),
    };

    const currentTimestamp = this.timestamp$.value;
    if (currentTimestamp) {
      updateData.timestamp = currentTimestamp;
    }

    // Clear the form state from store after updating
    this.clearFormState();

    this.updateMetrics.emit(updateData);
  }

  openTimestampDialog() {
    const dialogRef = this.dialog.open(OphTimestampDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: {timestamp: this.timestamp$.value || ''},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.timestamp$.next(result);
      }
    });
  }

  onOptionSelected(option: string, index: number) {
    this.form.at(index).get('value').setValue(option);
  }

  onMenuOpened(metricId: string) {
    this.openMenuId = metricId;
  }

  onMenuClosed() {
    this.openMenuId = null;
  }

  onUpdateValue(value: string, index: number) {
    this.form.at(index).get('value').setValue(value);
  }

  onStepperIncrease(metricIndex: number, event: Event) {
    const currentValue = this.form.at(metricIndex).get('value')?.value;
    const numericValue = currentValue ? parseFloat(currentValue) : 0;
    const newValue = numericValue + 1;
    this.form.at(metricIndex).get('value')?.setValue(newValue.toString());
  }

  onStepperDecrease(metricIndex: number, event: Event) {
    const currentValue = this.form.at(metricIndex).get('value')?.value;
    const numericValue = currentValue ? parseFloat(currentValue) : 0;
    const newValue = numericValue - 1;
    this.form.at(metricIndex).get('value')?.setValue(newValue.toString());
  }

  onSliderChange(event: Event, metricIndex: number) {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.form.at(metricIndex).get('value')?.setValue(value);
  }

  onContentEditableInput(event: Event, metricIndex: number) {
    const target = event.target as HTMLElement;
    let value = target.textContent || '';

    // Strip newlines so contenteditable stays single-line (e.g. from paste)
    const singleLine = value.replace(/[\r\n]+/g, '');
    if (singleLine !== value) {
      value = singleLine;
      target.textContent = value;
    }

    // If the value is empty, clear the innerHTML to ensure the element is truly empty for CSS :empty selector
    if (!value.trim()) {
      target.innerHTML = '';
    }

    this.form.at(metricIndex).get('value')?.setValue(value);
  }

  onContentEditableBlur(event: Event, metricIndex: number) {
    const target = event.target as HTMLElement;
    const value = target.textContent || '';

    // If the value is empty, clear the innerHTML to ensure the element is truly empty for CSS :empty selector
    if (!value.trim()) {
      target.innerHTML = '';
    }

    // Validate or format the value if needed
    this.form.at(metricIndex).get('value')?.setValue(value);
  }

  onTextInputFocus(metricIndex: number) {
    this.focusedTextMetricIndex.set(metricIndex);
  }

  onTextInputBlur(metricIndex: number) {
    this.focusedTextMetricIndex.set(null);
  }

  onTextInputChange(event: Event, metricIndex: number) {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.form.at(metricIndex).get('value')?.setValue(value);
  }

  onContentEditableKeydown(event: KeyboardEvent, metricIndex: number) {
    const metric = this.filteredMetrics[metricIndex];

    // For text metrics, allow all characters
    if (metric.type === TrackerMetricTypes.Text) {
      return;
    }

    // For numeric metrics, prevent Enter so contenteditable behaves like a single-line input
    if (event.key === 'Enter') {
      event.preventDefault();
      return;
    }

    // For numeric metrics, allow only numeric input and basic editing keys
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      '.',
      '-',
    ];

    if (allowedKeys.includes(event.key) || (event.key >= '0' && event.key <= '9') || event.ctrlKey || event.metaKey) {
      return;
    }

    event.preventDefault();
  }

  focusEditableValue(event: MouseEvent, metricIndex?: number) {
    // Find the container first
    const target = event.currentTarget as HTMLElement;
    const container = target.closest('.metric-container');

    // Check if this is a text metric by looking for the text-editable-value class
    const isTextMetric = container?.querySelector('.text-editable-value');

    // If this is a text metric, set focused state and focus the input after it renders
    if (isTextMetric && metricIndex !== undefined) {
      // Check if input already exists (already focused)
      const existingInput = container?.querySelector('.text-metric-input');
      if (existingInput) {
        // Input already exists, just ensure it's focused
        (existingInput as HTMLInputElement).focus();
        return;
      }

      // Input doesn't exist yet, create it by setting focused state
      this.focusedTextMetricIndex.set(metricIndex);
      setTimeout(() => {
        const input = container?.querySelector('.text-metric-input');
        if (input) {
          (input as HTMLInputElement).focus();
        }
      }, 0);
      return;
    }

    // Check if the click was directly on a numeric editable span
    const clickedElement = event.target as HTMLElement;
    if (clickedElement.classList.contains('editable-value') || clickedElement.tagName === 'INPUT') {
      // Let the default behavior handle cursor positioning for direct clicks on numeric metrics
      return;
    }

    // Otherwise, handle editable span (for numeric metrics)
    const editableSpan = container?.querySelector('.editable-value');
    if (editableSpan && 'focus' in editableSpan) {
      const element = editableSpan as HTMLElement;

      // Check if the element is already focused to avoid focus border glitch
      const isAlreadyFocused = document.activeElement === element;

      if (!isAlreadyFocused) {
        element.focus();
      }

      // Position cursor at the end of the text when clicking on non-editable elements
      // This allows clicking on the metric name, UOM, or container to focus and position cursor at end
      const range = document.createRange();
      const selection = window.getSelection();

      if (element.childNodes.length > 0) {
        range.setStartAfter(element.childNodes[element.childNodes.length - 1]);
      } else {
        range.setStart(element, 0);
      }

      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }

  private saveFormState() {
    if (!this.task?._id || !this.subtask?._id || !this.form.length) {
      return;
    }

    const formValues: MetricFormValue[] = this.form.value.map(item => ({
      metricId: item.metricId,
      value: item.value,
      readOnly: item.readOnly,
    }));

    const formState: SubtaskFormState = {
      formValues,
      timestamp: this.timestamp$.value || undefined,
    };

    this.store$.dispatch(
      new UpdateMetricFormStateAction({
        taskId: this.task._id,
        subtaskId: this.subtask._id,
        formState,
      })
    );
  }

  private loadFormState() {
    if (!this.task?._id || !this.subtask?._id) {
      return;
    }

    this.isLoadingState = true;

    this.store$
      .pipe(select(selectMetricFormState(this.task._id, this.subtask._id)), take(1))
      .subscribe((storedFormState: SubtaskFormState | null) => {
        if (storedFormState) {
          // Load timestamp
          if (storedFormState.timestamp) {
            this.timestamp$.next(storedFormState.timestamp);
          }

          // Load form values
          storedFormState.formValues.forEach((storedValue, index) => {
            const formControl = this.form.at(index);
            if (formControl && formControl.get('metricId')?.value === storedValue.metricId) {
              formControl.get('value')?.setValue(storedValue.value);
            }
          });
        }

        // Reset the loading flag after a brief delay to ensure all setValue operations are complete
        setTimeout(() => {
          this.isLoadingState = false;
        }, 0);
      });
  }

  private clearFormState() {
    if (!this.task?._id || !this.subtask?._id) {
      return;
    }

    this.store$.dispatch(
      new ClearMetricFormStateAction({
        taskId: this.task._id,
        subtaskId: this.subtask._id,
      })
    );
  }

  /**
   * Syncs editable span elements with their corresponding form values.
   * Only Basic numeric and Text metrics have editable spans.
   */
  private updateEditableSpanValues(): void {
    this.filteredMetrics.forEach((metric, formIndex) => {
      const span = this.findEditableSpanForMetric(metric, formIndex);
      if (!span) return;

      const formValue = this.form.at(formIndex)?.get('value')?.value;
      this.setSpanContent(span, formValue);
    });
  }

  private isBasicNumericMetric(metric: TrackerMetric): boolean {
    return metric.type === TrackerMetricTypes.Numeric && metric.numericType === MetricNumericType.Basic;
  }

  private isTextMetric(metric: TrackerMetric): boolean {
    return metric.type === TrackerMetricTypes.Text;
  }

  /**
   * Finds the editable span element for a given metric.
   * Returns undefined if the metric type doesn't have an editable span.
   */
  private findEditableSpanForMetric(metric: TrackerMetric, formIndex: number): ElementRef | undefined {
    if (this.isBasicNumericMetric(metric)) {
      const spanIndex = this.countPrecedingMetricsOfType(formIndex, this.isBasicNumericMetric.bind(this));
      return this.editableSpansNumeric?.toArray()[spanIndex];
    }

    if (this.isTextMetric(metric)) {
      const spanIndex = this.countPrecedingMetricsOfType(formIndex, this.isTextMetric.bind(this));
      return this.editableSpansText?.toArray()[spanIndex];
    }

    return undefined;
  }

  /**
   * Counts how many metrics of a specific type appear before the given index.
   * Used to map form indices to ViewChildren array indices.
   */
  private countPrecedingMetricsOfType(formIndex: number, typePredicate: (m: TrackerMetric) => boolean): number {
    return this.filteredMetrics.slice(0, formIndex).filter(typePredicate).length;
  }

  /**
   * Sets the content of an editable span element.
   * Clears innerHTML when empty to support CSS :empty selector.
   */
  private setSpanContent(span: ElementRef, value: unknown): void {
    if (!span?.nativeElement) return;

    const hasValue = value !== undefined && value !== null && String(value).trim() !== '';

    if (hasValue) {
      span.nativeElement.textContent = String(value);
    } else {
      span.nativeElement.innerHTML = '';
    }
  }
}
